<template>
    <section id="availability" class="scroll-mt-24">
        <h3 class="text-lg font-semibold mb-6">{{ $t('Select arrival date') }}</h3>
        <div class="flex gap-6 lg:flex-row flex-col">
            <div class="availability flex-1 relative">
                <DatePicker v-model="modelValue" :range="{ autoRange: form.nights }" inline multi-calendars auto-apply :formats="{ input: 'dd.MM.yyyy' }" hide-offset-dates :ui="{ dayClass }" :disabled-dates="isDisabled" :time-config="{ enableTimePicker: false }" model-type="yyyy-MM-dd" prevent-min-max-navigation :min-date="new Date()" :max-date="new Date(new Date().setFullYear(new Date().getFullYear() + 1))" @update:model-value="onDateSelected" @invalid-date="onInvalidDate">
                    <template #day="{ day, date }">
                        <span>{{ day }}</span>
                        <span class="dp--day-status">
                            <Icon v-if="!isDisabled(date)" name="check" size="xs" class="text-green-500" />
                            <span v-else class="dp--day-unavailable">-</span>
                        </span>
                    </template>
                </DatePicker>
                <DatePickerLegend />
            </div>
            <div class="block xl:hidden">
                <Booking :item="item" />
            </div>
        </div>
    </section>
</template>

<script setup>
import { useProductPriceAvailabilityStore } from '@/stores/productPriceAvailability'
import { useBookingStore } from '@/stores/booking'
import { storeToRefs } from 'pinia'
import { toIso, findNearestAvailableDate } from '@/utils/dateUtils'
import Booking from './Booking.vue'
import DatePickerLegend from '@/components/shared/DatePickerLegend.vue'
import { useToast } from '@/composables/toast/useToast.js'
import { useI18n } from 'vue-i18n'
import moment from 'moment'

defineProps({
    item: {
        type: Object,
        required: true
    }
})

const store = useProductPriceAvailabilityStore()
const bookingStore = useBookingStore()
const toast = useToast()
const { t, d } = useI18n()

const { availableDates } = storeToRefs(store)

const { form } = storeToRefs(bookingStore)

const buildRange = (startDate, nights) => (startDate ? [startDate, moment(startDate).add(nights, 'days').format('YYYY-MM-DD')] : null)

const modelValue = ref(buildRange(form.value.startDate, form.value.nights))

watch(
    () => [form.value.startDate, form.value.nights],
    ([startDate, nights]) => {
        modelValue.value = buildRange(startDate, nights)
    }
)

const showUnavailableToast = (start) => {
    const nearest = findNearestAvailableDate(start, availableDates.value)
    toast.warning(t('Arrival on this date is not possible. Nearest possible arrival: :nearest_date', { date: d(nearest, 'full') }))
}

const onDateSelected = (value) => {
    const start = Array.isArray(value) ? value[0] : value

    if (!availableDates.value.includes(start)) {
        modelValue.value = buildRange(form.value.startDate, form.value.nights)
        showUnavailableToast(start)
        return
    }

    form.value.startDate = start
}

/** Fires when the user clicks a disabled (unavailable) calendar day */
const onInvalidDate = (date) => {
    const iso = toIso(date)

    if (!availableDates.value.includes(iso)) {
        showUnavailableToast(iso)
    }
}

/** Enable only available startDates */
const isDisabled = (date) => {
    const iso = toIso(date)
    return !availableDates.value.includes(iso)
}

/** Highlight only available start dates */
const dayClass = (day) => {
    const iso = toIso(day)
    return availableDates.value.includes(iso) ? 'available-date' : ''
}
</script>
